import type { IconPickerSelection } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';

export interface ParsedAccordionIcon {
  type: 'emoji' | 'fa' | 'img';
  value: string;
  color?: string;
  showBackground?: boolean;
}

/**
 * Serialize an IconPickerSelection to a string for storage as a TipTap node attribute.
 *
 * Format:
 *   emoji  → unicode character (e.g. "😊")
 *   fa     → "fa:icon-name:#color" (e.g. "fa:star:#ef4444")
 *   fa+bg  → "fa:icon-name:#color:bg" (e.g. "fa:star:#ef4444:bg")
 *   img    → "img:path/to/icon.png"
 */
export function serializeAccordionIcon(selection: IconPickerSelection): string {
  switch (selection.type) {
    case 'emoji':
      return selection.emoji.emoji ?? selection.emoji.name;
    case 'fa': {
      const base = `fa:${selection.name}:${selection.color}`;
      return selection.showBackground ? `${base}:bg` : base;
    }
    case 'img':
      return `img:${selection.src}`;
  }
}

/**
 * Convert an icon color to a translucent background color.
 */
export function getIconBgColor(color?: string): string | null {
  if (!color || color === 'currentColor') {
    return 'rgba(0, 0, 0, 0.06)';
  }
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
}

/**
 * Parse a serialized icon string back into a structured object.
 * Returns null for empty strings.
 */
export function parseAccordionIcon(icon: string): ParsedAccordionIcon | null {
  if (!icon) {
    return null;
  }

  if (icon.startsWith('fa:')) {
    const parts = icon.split(':');
    const name = parts[1];
    const color = parts[2] || 'currentColor';
    const showBackground = parts[3] === 'bg';
    return { type: 'fa', value: name, color, showBackground };
  }

  if (icon.startsWith('img:')) {
    return { type: 'img', value: icon.slice(4) };
  }

  return { type: 'emoji', value: icon };
}
